<template>
  <div class="package-tracker">
    <LoadingComponent class="loading-component" v-if="state === states.LOADING"></LoadingComponent>

    <div v-else-if="state === states.SHOWING_ERROR" class="package-message">
      <SearchInput :country="countryLocal" :trackingNumber="trackingNumberLocal" @search="onSearch"></SearchInput>
      <ErrorDisplay :error="error"></ErrorDisplay>
    </div>

    <div v-else class="package-message md-scroll" :class="{ 'overflow-visible': isCollapsed(), 'overflow-y-auto': !isCollapsed() }">
      <SearchInput :country="countryLocal" :trackingNumber="trackingNumberLocal" @search="onSearch"></SearchInput>

      <transition name="collapse-message">
        <div class="message-animation">
          <div class="primary-message">{{ successHeaderMessage }}</div>
          <div class="secondary-message" v-if="isDeliveryDateAvailable">{{ deliveryDateMessage }}</div>
          <div class="secondary-message" v-if="!isRouteDrawable">{{ $t('PACKAGE.ROUTE.UNABLE_TO_DRAW') }}</div>
        </div>
      </transition>

      <div @click="onCollapseClick" class="cursor-pointer" v-show="state === states.SHOWING_PACKAGE">
        <md-icon>keyboard_arrow_down</md-icon>
        <span class="md-caption">{{ $t('LIST_AREA.VIEW_DETAILS_PLACEHOLDER') }}</span>
      </div>
      <div @click="onCollapseClick" class="cursor-pointer" v-show="state === states.SHOWING_TIMELINE">
        <md-icon>keyboard_arrow_up</md-icon>
        <span class="md-caption">{{ $t('LIST_AREA.COLLAPSE_PLACEHOLDER') }}</span>
      </div>
      <div @click="onCollapseClick" class="cursor-pointer" v-show="state === states.SHOWING_HUB_DETAILS">
        <md-icon>keyboard_arrow_left</md-icon>
        <span class="md-caption">{{ $t('LIST_AREA.BACK_TO_PATH_DETAILS') }}</span>
      </div>

      <transition name="collapse-timeline">
        <div class="timeline-animation" v-show="state === states.SHOWING_TIMELINE || state === states.SHOWING_HUB_DETAILS">
          <TimeLine class="small-top-margin" v-if="state !== states.SHOWING_HUB_DETAILS" @hubClick="onTimeLineHubClick"></TimeLine>
          <transition name="collapse-hub-details">
            <div class="hub-details-animation" v-show="state === states.SHOWING_HUB_DETAILS" v-if="selectedHub">
              <HubDetails class="top-margin"
                  :trackingNumber="trackingNumberLocal" :hubDetails="selectedHub">
              </HubDetails>
            </div>
          </transition>
        </div>
      </transition>
    </div>

  </div>
</template>

<script>
import LoadingComponent from '@/components/LoadingComponent.vue'
import SearchInput from '@/components/SearchInput'
import TimeLine from '@/components/TimeLine'
import HubDetails from '@/components/HubDetails'
import ErrorDisplay from '@/components/ErrorDisplay'
import { COUNTRIES, ERROR_REASONS } from '@/configs/constants'
import { mapActions, mapGetters } from 'vuex'

const COMPONENT_STATES = {
  LOADING: 'LOADING',
  SHOWING_ERROR: 'SHOWING_ERROR',
  SHOWING_PACKAGE: 'SHOWING_PACKAGE',
  SHOWING_TIMELINE: 'SHOWING_TIMELINE',
  SHOWING_HUB_DETAILS: 'SHOWING_HUB_DETAILS'
}

export default {
  name: 'PackageTracker',
  components: {
    LoadingComponent,
    SearchInput,
    TimeLine,
    HubDetails,
    ErrorDisplay
  },
  props: {
    trackingNumber: { type: String, required: true },
    country: { type: String, required: false, default: 'ng' }
  },
  data () {
    return {
      trackingNumberLocal: this.trackingNumber,
      countryLocal: this.country,
      states: COMPONENT_STATES,
      state: COMPONENT_STATES.LOADING,
      selectedHub: null,
      error: null
    }
  },
  computed: {
    ...mapGetters('package', [
      'lastEvent',
      'isRouteDrawable',
      'isDeliveryDateAvailable',
      'deliveryDateMessage'
    ]),
    successHeaderMessage () {
      return this.lastEvent ? this.lastEvent.label : this.$t('PACKAGE.STATUS.WORKING_ON_IT')
    }
  },
  watch: {
    $route (to, _from) {
      const trackingNumber = to.params.trackingNumber
      const country = to.params.country

      if (this.isParametersValidAndDifferentFromLocal({ country, trackingNumber })) {
        this.resetLocalState({ trackingNumber, country })
        this.removeHubs()
        this.loadPackageAndDisplayHubs()
      }
    }
  },
  methods: {
    ...mapActions('package', [
      'loadPackage'
    ]),
    ...mapActions('map', [
      'displayHubs',
      'removeHubs',
      'zoomAndCenterHubs',
      'centerMapOnHub',
      'centerMapOnDefaultPosition',
      'bounceHub'
    ]),
    loadPackageAndDisplayHubs () {
      this.state = COMPONENT_STATES.LOADING
      this.error = null

      if (!this.isCountryValid(this.countryLocal)) {
        this.error = { reason: ERROR_REASONS.INVALID_COUNTRY }
        this.state = COMPONENT_STATES.SHOWING_ERROR
        this.centerMapOnDefaultPosition()
        return
      }

      this.loadPackage({ country: this.countryLocal, trackingNumber: this.trackingNumberLocal })
        .then(packagePayload => {
          this.state = COMPONENT_STATES.SHOWING_TIMELINE
          if (this.isRouteDrawable) {
            this.displayHubs({ packagePayload, hubClickCallback: this.hubClickCallback })
            this.zoomAndCenterHubs({ packagePayload })
          }
          return Promise.resolve()
        })
        .catch(error => {
          this.error = error
          this.state = COMPONENT_STATES.SHOWING_ERROR
          this.centerMapOnDefaultPosition()
        })
    },
    hubClickCallback (hubPayload) {
      this.selectedHub = hubPayload
      this.state = COMPONENT_STATES.SHOWING_HUB_DETAILS
    },
    onTimeLineHubClick ({ step }) {
      this.hubClickCallback(step)
      if (this.isRouteDrawable) {
        this.centerMapOnHub({ step })
        this.bounceHub({ step })
      }
    },
    onCollapseClick () {
      if (this.state === COMPONENT_STATES.SHOWING_HUB_DETAILS ||
          this.state === COMPONENT_STATES.SHOWING_PACKAGE) {
        this.state = COMPONENT_STATES.SHOWING_TIMELINE
      } else {
        this.state = COMPONENT_STATES.SHOWING_PACKAGE
      }
    },
    onSearch ({ trackingNumber, country }) {
      if (this.isParametersValidAndDifferentFromLocal({ trackingNumber, country })) {
        this.resetLocalState({ trackingNumber, country })
        this.$router.push({ params: { country, trackingNumber } })
        this.removeHubs()
        this.loadPackageAndDisplayHubs()
      }
    },
    isParametersValidAndDifferentFromLocal ({ country, trackingNumber }) {
      const isTrackingNumberAndCountryDefined = trackingNumber && country
      const isTrackingNumberOrCountryDifferentFromLocal = trackingNumber !== this.trackingNumberLocal || country !== this.countryLocal
      return isTrackingNumberAndCountryDefined && isTrackingNumberOrCountryDifferentFromLocal
    },
    resetLocalState ({ trackingNumber, country }) {
      this.selectedHub = null
      this.trackingNumberLocal = trackingNumber
      this.countryLocal = country
    },
    isCountryValid (countryCode) {
      if (!countryCode || typeof countryCode !== 'string') {
        return false
      }
      return (COUNTRIES[countryCode.toUpperCase()] !== undefined)
    },
    isCollapsed () {
      return (this.state === COMPONENT_STATES.SHOWING_ERROR || this.state === COMPONENT_STATES.SHOWING_PACKAGE)
    }
  },
  mounted () {
    this.loadPackageAndDisplayHubs()
  },
  beforeDestroy () {
    this.removeHubs()
  }
}
</script>

<style lang="sass" scoped>
.package-message
  background-color: rgba(255, 255, 255, 0.7)
  border-radius: 10px
  padding: 25px
  position: absolute
  z-index: 10
  margin: 4% 0 0 4%
  width: 40%
  max-height: 80%
  @media (max-width: $large-device-width)
    width: 60%
  @media (max-width: $medium-device-width)
    width: 84%

.top-margin
  margin-top: 30px
.small-top-margin
  margin-top: 5px

.cursor-pointer
  cursor: pointer

.message-animation
  margin-bottom: 30px
  max-height: 500px
  overflow: hidden
.collapse-message-enter-active, .collapse-message-leave-active
  transition: max-height .6s
.collapse-message-enter, .collapse-message-leave-to /* .fade-leave-active below version 2.1.8 */
  max-height: 0

.timeline-animation
  max-height: 1000px
  overflow: hidden
.collapse-timeline-enter-active
  transition: max-height .8s
.collapse-timeline-leave-active
  transition: max-height .6s
.collapse-timeline-enter, .collapse-timeline-leave-to /* .fade-leave-active below version 2.1.8 */
  max-height: 0

.hub-details-animation
  max-width: 500px
  overflow: hidden
.collapse-hub-details-enter-active
  transition: max-width .6s
.collapse-hub-details-enter /* .fade-leave-active below version 2.1.8 */
  max-width: 0

.overflow-visible
  overflow: visible

.overflow-y-auto
  overflow-y: auto
</style>
